
import { Component, Prop, Vue } from "vue-property-decorator";
import {
  ICustomerEmployeeDataToShow,
  ISolCustomerEmployee,
  ISolCustomerEmployeeResponse,
  ISolCustomerImageParams,
  ISolCustomerImageResponse,
  ISolCustomerImageType,
} from "@/services";
import { Action, Getter } from "vuex-class";
import { ICountryConfig } from "@/entities";

@Component({
  name: "firms-employee",
  components: {},
})
export default class FirmsEmployee extends Vue {
  @Getter("getCountryConfig") countryConfig!: ICountryConfig | undefined;

  @Prop() public employeeResponse!: ISolCustomerEmployeeResponse | undefined;
  @Prop() public country!: string | undefined;
  @Prop() public searchType!: string | undefined;

  @Action("fetchImage") fetchImage!: (
    params: ISolCustomerImageParams
  ) => Promise<ISolCustomerImageResponse | undefined>;

  employeePhotos: { registry: string; photo: string }[] = [];
  signaturePhotos: { registry: string; signature: string }[] = [];

  isDataProtectedByLaw: undefined | boolean = false;
  showDataDocument: undefined | boolean = false;

  employeeDataToShow: {
    registry: string;
    show: ICustomerEmployeeDataToShow;
  }[] = [];

  constructor() {
    super();
  }

  findPhotoByRegistry = (registry: string): string | undefined => {
    const result = this.employeePhotos.find((photos) => photos.registry === registry);
    return result?.photo;
  };

  findSignatureByRegistry = (registry: string): string | undefined => {
    const result = this.signaturePhotos.find((signature) => signature.registry === registry);
    return result?.signature;
  };

  isPictureVisible = (emp: ISolCustomerEmployee): boolean =>
    !!this.findEmployeeDataToShow(emp)?.picture;
  isSignVisible = (emp: ISolCustomerEmployee): boolean => !!this.findEmployeeDataToShow(emp)?.sign;
  isRoleVisible = (emp: ISolCustomerEmployee): boolean => !!this.findEmployeeDataToShow(emp)?.role;
  isIdVisible = (emp: ISolCustomerEmployee): boolean => !!this.findEmployeeDataToShow(emp)?.id;
  isNameVisible = (emp: ISolCustomerEmployee): boolean => !!this.findEmployeeDataToShow(emp)?.name;

  findEmployeeDataToShow = (emp: ISolCustomerEmployee): ICustomerEmployeeDataToShow | undefined => {
    const result = this.employeeDataToShow.find(
      (dataToShow) => dataToShow.registry === emp.registry
    );
    return result?.show;
  };

  // Used this part of lifecycle to put the data available to the template
  async created(): Promise<void> {
    await this.loadDataProtectionConfig().then(() => this.loadDataEmployee());
  }

  async mounted(): Promise<void> {
    await this.loadDataProtectionConfig().then(() => this.loadDataEmployee());
  }

  loadDataEmployee(): void {
    this.employeeResponse?.employees.forEach((emp) => this.fetchDataEmployee(emp));
  }

  async fetchDataEmployee(emp: ISolCustomerEmployee): Promise<void> {
    const showData = this.findEmployeeDataToShow(emp);
    if (showData?.sign) await this.fetchEmployeeSign(emp);

    if (showData?.picture) await this.fetchEmployeeImage(emp);
  }

  async fetchEmployeeImage(emp: ISolCustomerEmployee): Promise<void> {
    let photo = await this.fetchImage({
      country: this.country!,
      p: emp.registry,
      type: ISolCustomerImageType.Person,
      token: new URLSearchParams(emp.photoURL).get("token")!,
    });
    this.employeePhotos.push({
      registry: emp.registry,
      photo: `data:image/png;base64, ${photo?.photo}`,
    });
  }

  async fetchEmployeeSign(emp: ISolCustomerEmployee): Promise<void> {
    let signature = await this.fetchImage({
      country: this.country!,
      p: emp.registry,
      type: ISolCustomerImageType.Signature,
      token: new URLSearchParams(emp.signatureURL).get("token")!,
    });
    this.signaturePhotos.push({
      registry: emp.registry,
      signature: `data:image/png;base64, ${signature?.signature}`,
    });
  }

  async loadDataProtectionConfig(): Promise<void> {
    this.isDataProtectedByLaw = this.countryConfig?.SearchEmployee.DataProtectionLawConfig;
    this.showDataDocument = this.countryConfig?.SearchEmployee.ShowDataDocument;
    this.employeeResponse?.employees.forEach((emp) =>
      this.saveEmployeeDataToShowProtectedByRole(emp)
    );
  }

  saveEmployeeDataToShowProtectedByRole(employee: ISolCustomerEmployee): void {
    let configuration;

    if (this.isDataProtectedByLaw) {
      configuration = this.hasPortavalorRole(employee)
        ? this.getConfigForPortavalor()
        : this.getConfigForOtherRoles();
    } else {
      configuration = this.getDataToShowAllInfo();
    }

    this.employeeDataToShow?.push({ registry: employee.registry, show: configuration });
  }

  hasPortavalorRole(employee: ISolCustomerEmployee): boolean {
    const EMPLOYEE_FUNCTION_TO_CHECK = ["Portavalor", "Porteador", "Porta"];
    return (
      EMPLOYEE_FUNCTION_TO_CHECK.find(
        (role) => role.toLowerCase() == employee.function.toLowerCase()
      ) != undefined
    );
  }

  private getConfigForOtherRoles(): ICustomerEmployeeDataToShow {
    return {
      id: true,
      name: false,
      picture: false,
      role: true,
      sign: false,
    };
  }

  private getConfigForPortavalor(): ICustomerEmployeeDataToShow {
    return {
      id: true,
      name: false,
      picture: true,
      role: true,
      sign: true,
    };
  }

  private getDataToShowAllInfo(): ICustomerEmployeeDataToShow {
    return {
      id: true,
      name: true,
      picture: true,
      role: true,
      sign: true,
    };
  }

  getKey(emp: ISolCustomerEmployee): string {
    return this.searchType === "workBadge" ? emp.workBadge : emp.registry;
  }

  getTitle(emp: ISolCustomerEmployee): string {
    if (emp.function) {
      return emp.function + " - " + this.getKey(emp);
    } else {
      return this.getKey(emp);
    }
  }
}
